// Simple JWT Token Strategy for MVP
import { Injectable, inject } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { Router } from '@angular/router';
import { AuthSession, AuthTokens, User, AuthError } from '../models/auth.models';

/**
 * Simple JWT Token Strategy for MVP
 * Handles JWT tokens from URL parameter with client-side validation
 * Simplified without abstract class inheritance for MVP clarity
 */
@Injectable({
  providedIn: 'root'
})
export class SimpleJwtTokenStrategy {
  readonly strategyName = 'simple-jwt';
  private router = inject(Router);

  /**
   * Initialize strategy by checking URL for token parameter
   */
  initialize(): Observable<AuthSession | null> {
    const currentUrl = window.location.href;
    const token = this.extractTokenFromUrl(currentUrl);

    if (!token) {
      return of(null);
    }

    return this.validateJwtToken(token);
  }

  /**
   * Extract JWT token from URL parameter
   */
  private extractTokenFromUrl(url: string): string | null {
    try {
      const urlObj = new URL(url);
      const token = urlObj.searchParams.get('token');

      if (token) {
        // Clean URL after extracting token
        this.cleanUrlParameters();
      }

      return token;
    } catch {
      return null;
    }
  }

  /**
   * Validate JWT token (client-side for MVP) - Public method for manual authentication
   */
  validateJwtToken(token: string): Observable<AuthSession> {
    return new Observable(observer => {
      // Simulate network delay
      setTimeout(() => {
        try {
          // Decode JWT token (without signature verification for MVP)
          const payload = this.decodeJwtPayload(token);

          // Validate token
          const validationResult = this.isTokenValid(payload);
          if (!validationResult.isValid) {
            observer.error({
              status: 401,
              message: validationResult.reason || 'Token inválido o expirado',
              code: validationResult.code || 'TOKEN_INVALID'
            });
            return;
          }

          // Create session from JWT payload
          const session = this.createSessionFromJwt(token, payload);
          observer.next(session);
          observer.complete();

        } catch (error) {
          observer.error({
            status: 500,
            message: error instanceof Error ? error.message : 'Error validando token JWT',
            code: 'TOKEN_DECODE_ERROR'
          });
        }
      }, 800); // Simulate network delay
    });
  }

  /**
   * Decode JWT payload (Base64 decode without signature verification)
   */
  private decodeJwtPayload(token: string): any {
    try {
      // JWT structure: header.payload.signature
      const parts = token.split('.');
      if (parts.length !== 3) {
        throw new Error('Token JWT inválido');
      }

      // Decode base64 payload
      const payload = parts[1];
      // Add padding if needed
      const padded = payload + '='.repeat((4 - payload.length % 4) % 4);
      const decoded = atob(padded.replace(/-/g, '+').replace(/_/g, '/'));

      return JSON.parse(decoded);
    } catch {
      throw new Error('No se pudo decodificar el token JWT');
    }
  }

  /**
   * Validate JWT token payload
   */
  private isTokenValid(payload: any): { isValid: boolean; reason?: string; code?: string } {
    // Check if token has required fields (adjusted for real SmartLinks token structure)
    if (!payload.sub || !payload.email) {
      return {
        isValid: false,
        reason: 'El token no contiene la información requerida (usuario y email)',
        code: 'MISSING_REQUIRED_FIELDS'
      };
    }

    // Check if token is expired (required check)
    const now = Math.floor(Date.now() / 1000);
    if (!payload.exp) {
      return {
        isValid: false,
        reason: 'El token no tiene tiempo de expiración',
        code: 'MISSING_EXPIRATION'
      };
    }

    if (payload.exp < now) {
      return {
        isValid: false,
        reason: 'El token de autenticación ha expirado',
        code: 'TOKEN_EXPIRED'
      };
    }

    // Check RT claim validation (CRITICAL: Only "ag" and "ai" are allowed)
    const allowedRtValues = ['ag', 'ai', 'at'];
    if (!payload.rt) {
      return {
        isValid: false,
        reason: 'El token no contiene el claim de rol requerido (rt)',
        code: 'MISSING_RT_CLAIM'
      };
    }

    if (!allowedRtValues.includes(payload.rt)) {
      return {
        isValid: false,
        reason: `El rol del token (${payload.rt}) no está autorizado. Roles permitidos: ${allowedRtValues.join(', ')}`,
        code: 'INVALID_RT_CLAIM'
      };
    }

    // Optional: Check if token is not yet valid (iat not always present in real tokens)
    if (payload.iat && payload.iat > now) {
      return {
        isValid: false,
        reason: 'El token de autenticación aún no es válido',
        code: 'TOKEN_NOT_YET_VALID'
      };
    }

    return { isValid: true };
  }

  /**
   * Create authentication session from JWT payload
   */
  private createSessionFromJwt(token: string, payload: any): AuthSession {
    // Extract user info from real SmartLinks JWT payload
    const user: User = {
      id: payload.userId || payload.sub,
      email: payload.email || payload.userName,
      name: payload.name || payload.sub,
      username: payload.userName || payload.email,
      roles: [], // No roles for simplified MVP
      permissions: [], // No permissions for simplified MVP
      avatar: `https://ui-avatars.com/api/?name=${encodeURIComponent(payload.name || payload.sub)}&background=667eea&color=fff`,
      lastLogin: new Date(),
      sessionId: payload.jti || `jwt-${payload.exp}`, // Use JWT ID if available
      // Additional SmartLinks specific fields
      agencyId: payload.agencyId,
      agencyName: payload.agencyName,
      culture: payload.culture || 'es-ES',
      gender: payload.gender,
      tenant: {
        appId: payload['tenant.appId'],
        agencyId: payload['tenant.agencyId'],
        marketPlaceId: payload['tenant.marketPlaceId'],
        experienceCenterId: payload['tenant.experienceCenterId'],
        compositeKey: payload['tenant.compositeKey']
      }
    };

    // Create tokens
    const tokens: AuthTokens = {
      accessToken: token,
      refreshToken: '', // No refresh token for MVP
      expiresIn: (payload.exp - Math.floor(Date.now() / 1000)) * 1000, // Convert to milliseconds
      tokenType: 'Bearer'
    };

    // Create session
    return {
      user,
      tokens,
      isAuthenticated: true,
      loginMethod: 'simple-jwt',
      sessionStarted: new Date(),
      lastActivity: new Date()
    };
  }

  /**
   * Extract name from email
   */
  private extractNameFromEmail(email: string): string {
    if (!email) return 'Usuario';

    const parts = email.split('@');
    if (parts.length === 0) return 'Usuario';

    const name = parts[0].replace(/[._-]/g, ' ');
    return name.charAt(0).toUpperCase() + name.slice(1);
  }

  /**
   * Clean URL parameters after successful authentication
   */
  private cleanUrlParameters(): void {
    const url = new URL(window.location.href);
    url.searchParams.delete('token');

    // Update URL without reloading
    window.history.replaceState({}, '', url.toString());
  }

  /**
   * Authenticate with credentials (not used in this strategy)
   */
  authenticate(credentials: any): Observable<AuthSession> {
    return throwError(() => ({
      code: 'NOT_SUPPORTED',
      message: 'Use URL token for authentication'
    }));
  }

  /**
   * Refresh tokens (not supported in MVP)
   */
  refreshTokens(tokens: AuthTokens): Observable<AuthTokens> {
    return throwError(() => ({
      code: 'NOT_SUPPORTED',
      message: 'Token refresh not supported in MVP'
    }));
  }

  /**
   * Logout user
   */
  logout(): Observable<void> {
    return of(void 0);
  }

  /**
   * Validate current session
   */
  validateSession(session: AuthSession): Observable<boolean> {
    if (!session?.tokens?.accessToken) {
      return of(false);
    }

    try {
      const payload = this.decodeJwtPayload(session.tokens.accessToken);
      const validationResult = this.isTokenValid(payload);
      return of(validationResult.isValid);
    } catch {
      return of(false);
    }
  }

  /**
   * Handle authentication errors
   */
  handleError(error: any): AuthError {
    if (error.status === 401) {
      return {
        code: 'UNAUTHORIZED',
        message: 'Token inválido o expirado',
        details: error
      };
    }

    return {
      code: 'AUTH_ERROR',
      message: error.message || 'Error de autenticación',
      details: error
    };
  }
}
